/**
 * Reading the tour outcome breakdown.
 *
 * @module Pages/BUs/Tours
 */

import type { TourStatusBreakdown, TourStepBucket } from '../Types/Tours.types';

/** One slice of the outcome bar, ready to draw. */
export interface OutcomeSlice {
  key: keyof TourStatusBreakdown;
  label: string;
  count: number;
  /** Share of the reported views, 0–100. Rounded for display only. */
  percentage: number;
  color: string;
}

const SLICE_ORDER: { key: keyof TourStatusBreakdown; label: string; color: string }[] = [
  { key: 'completed', label: 'Concluíram', color: 'var(--color-success)' },
  { key: 'abandoned', label: 'Abandonaram', color: 'var(--color-warning)' },
  { key: 'skipped', label: 'Pularam', color: 'var(--color-text-muted)' },
  { key: 'unknown', label: 'Sem registro', color: 'var(--color-border-strong)' },
];

/**
 * The breakdown as drawable slices, in a fixed order.
 *
 * Percentages are over the **total of the breakdown**, not over `viewCount`: a view whose outcome
 * was never reported is still a view, and dividing by a larger denominator would make the four
 * shares silently fail to add up.
 *
 * Empty slices are kept rather than filtered, so the legend does not reshuffle between tours.
 */
export function getOutcomeSlices(breakdown: TourStatusBreakdown | undefined): OutcomeSlice[] {
  const safe = breakdown ?? { completed: 0, abandoned: 0, skipped: 0, unknown: 0 };
  const total = safe.completed + safe.abandoned + safe.skipped + safe.unknown;

  return SLICE_ORDER.map(slice => ({
    ...slice,
    count: safe[slice.key],
    percentage: total === 0 ? 0 : Math.round((safe[slice.key] / total) * 100),
  }));
}

/** Whether there is any reported outcome at all — otherwise the breakdown says nothing. */
export function hasReportedOutcomes(breakdown: TourStatusBreakdown | undefined): boolean {
  if (!breakdown) return false;
  return breakdown.completed + breakdown.abandoned + breakdown.skipped > 0;
}

/**
 * The step where most people stopped, or `undefined` when nothing was reported.
 *
 * Ties resolve to the earliest step: it is the more actionable reading — the first place the tour
 * loses people is the one worth fixing.
 */
export function getDropOffStep(
  histogram: TourStepBucket[] | undefined,
): TourStepBucket | undefined {
  if (!histogram || histogram.length === 0) return undefined;

  return histogram.reduce((worst, bucket) => (bucket.viewers > worst.viewers ? bucket : worst));
}
